import { format } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { HttpEvent, HttpEventType } from '@angular/common/http';
import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { SnAuditOperation, SnCamera } from '@lib/api/entities';
import { SnAuditService } from '@lib/api/services';
import { SnSessionService } from '@lib/auth';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { TimelineService } from '@lib/timeline';
import { t } from '@lib/translation';
import { SnContentType } from 'libs/api/entities/src/lib/base';

@Component({
  selector: 'fs-download-video',
  templateUrl: './download-video.component.html',
  styleUrls: ['./download-video.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DownloadVideoComponent implements OnInit, OnDestroy {
  @Input() public camera: SnCamera;
  @Input() public data = null;
  @Input() public isLocal = false;
  @Input() public errorTimer = false;
  @Input() public start = null;
  @Input() public end = null;
  @Input() public invalid = false;

  public clipLoading: boolean;
  public isDownloading = false;
  public percentDone = 0;
  public secondsLeft = 0;
  public fileSize = 0;
  public downloadedSize = 0;
  public exportDisabled: boolean;

  @Output() public readonly loadingInProgress = new EventEmitter<boolean>();

  private readonly _destroy$ = new Subject<void>();
  private _cancel$: Subject<void> = new Subject();

  constructor(
    private readonly _timelineService: TimelineService,
    private readonly _changeDetector: ChangeDetectorRef,
    private readonly _toastrService: ToastrService,
    private readonly _datadogService: FsDataDogRumService,
    private readonly _sessionService: SnSessionService,
    private readonly _auditService: SnAuditService,
  ) {}

  get isEagleEye(): boolean {
    if (!this.camera?.externalType) {
      return;
    }
    return this.camera.externalType?.toLowerCase() === 'eagleeyenetworks';
  }

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public checkTime(): void {
    const start = this.start ? this.start : Math.floor(this.data.startTime / 1000);
    const end = this.end ? this.end : Math.ceil(this.data.endTime / 1000);
    if (end - start >= 21600) {
      this.exportDisabled = true;
    }
  }

  public downloadClip(): void {
    const start = this.start ? this.start : Math.floor(this.data.startTime / 1000);
    const end = this.end ? this.end : Math.ceil(this.data.endTime / 1000);

    // Reset any previous error states
    this.exportDisabled = false;

    this.checkTime();

    if (!this.exportDisabled) {
      const url = this._genExportUrl(start, end);
      const a = document.createElement('a');
      document.body.appendChild(a);
      this.clipLoading = true;
      this.loadingInProgress.emit(this.clipLoading);
      this.isDownloading = true;
      this.secondsLeft = 0;
      this.percentDone = 0;
      this.fileSize = 0;
      this.downloadedSize = 0;
      this._changeDetector.detectChanges();

      this._cancel$.next();

      const startDownloadTime = new Date();

      this._timelineService
        .export(url)
        .pipe(
          tap((event: HttpEvent<any>) => {
            if (event.type === HttpEventType.DownloadProgress) {
              this.fileSize = event.total;
              this.downloadedSize = event.loaded;
              this.percentDone = Math.round((100 * event.loaded) / event.total);
              this._changeDetector.detectChanges();
              const elapsedTime = new Date().getTime() - startDownloadTime.getTime();
              const chunkPerTime = event.loaded / elapsedTime;
              const estimatedTotalTime = event.total / chunkPerTime;
              const timeLeftInSeconds = (estimatedTotalTime - elapsedTime) / 1000;
              this.secondsLeft = Math.round(timeLeftInSeconds * 10) / 10;
            }
          }),
          takeUntil(this._destroy$),
          takeUntil(this._cancel$),
        )
        .subscribe({
          next: (r: HttpEvent<any>) => {
            if (r.type === HttpEventType.Response) {
              const blobUrl = window.URL.createObjectURL(r.body);
              a.href = blobUrl;
              a.download = `${this.camera.id}-${start}-${end}.mp4`;
              a.click();
              window.URL.revokeObjectURL(blobUrl);
              a.remove();
              this._resetDownloadState();
              this._toastrService.success(t('vault.downloadVideo.clipDownloaded'));
              this._datadogService.addAction('download_clip', {
                id: this.camera.id,
                name: this.camera.name,
                startTime: this.data.startDisplay,
                endTime: this.data.endDisplay,
              });
              this.auditDownloadClipVideo();
            }
          },
          error: (err) => {
            this._toastrService.error(t('vault.downloadVideo.unableToCreateClip'));
            console.error('Error creating clip.', err);
            this._resetDownloadState();
          },
        });
    } else {
      this._toastrService.error(t('vault.downloadVideo.cannotDownload6Hours'));
      this._resetDownloadState();
    }
  }

  public cancelDownload(): void {
    this._cancel$.next();
    this._resetDownloadState();
  }

  private _resetDownloadState(): void {
    this.isDownloading = false;
    this.clipLoading = false;
    this.loadingInProgress.emit(this.clipLoading);
    this._changeDetector.detectChanges();
  }

  public auditDownloadClipVideo(): void {
    const payload: any = {
      object: {
        id: this.camera.id,
        name: this.camera.name,
        contentType: SnContentType.Camera,
      },
      detail: {
        name: this.camera.name,
      },
      changeData: {
        startTime: this.data.startDisplay,
        endTime: this.data.endDisplay,
      },
      operation: SnAuditOperation.ClipDownloaded,
    };
    this._auditService
      .createAudit([payload])
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {});
  }

  private _genExportUrl(start: number, end: number): string {
    if (this._sessionService.isFederal) {
      return this.camera.prerecordingUrls.getFederalExportUrl(
        new Date(start * 1000),
        new Date(end * 1000),
      );
    }
    if (this.isEagleEye) {
      const baseUrl = this.camera.liveVideoUrls.main.cloud.mp4.split('.mp4?id=')[0];
      const esn = this.camera.prerecordingUrls.video.split('&')[0].split('=')[1];
      const headers = this.camera.liveVideoUrls.main.cloud.headers;
      const authKey = headers['Cookie']
        ?.split('; ')
        ?.find((value) => value?.includes('auth_key'))
        .split('=')[1];
      const startDate = format(
        new Date(start * 1000 + new Date(start * 1000).getTimezoneOffset() * 60000),
        'yyyyMMddHHmmss.SSS',
      );

      const endDate = format(
        new Date(end * 1000 + new Date(end * 1000).getTimezoneOffset() * 60000),
        'yyyyMMddHHmmss.SSS',
      );

      return `${baseUrl}.mp4?id=${esn}&start_timestamp=${startDate}&end_timestamp=${endDate}&A=${authKey}`;
    }
    const exportSrc = this.isLocal
      ? this.camera.playbackUrls.main.local.export
      : this.camera.playbackUrls.main.cloud.export;
    return `${exportSrc}?start=${start}&end=${end}`;
  }
}
